import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import {
  CHARACTER_IMPORT_OUTCOMES,
  CHARACTER_REGIONS,
  CHARACTER_REGION_LABELS,
  type CharacterImportOutcome,
  type CharacterRegion,
  type CharacterSummary,
  type MeCharactersResponse,
} from '@thottbot/shared';
import { apiSend, queryKeys, useMe, useMyCharacters } from '../../api/client';
import { relativeTime } from '../../util/time';

declare global {
  interface Window {
    /**
     * Playwright cannot follow the Battle.net redirect, so the e2e spec points the import form
     * at `/auth/characters/dev-import?...` through this override (mirrors `__thottbotWatch`).
     */
    __thottbotImportUrl?: string;
  }
}

const IMPORT_ACTION = '/auth/characters/import';

/**
 * The form's action and the fields it must carry. A GET form replaces the action's query string
 * with its own fields, so an override URL's query becomes hidden inputs.
 */
function importAction(): { action: string; fields: [string, string][]; overridden: boolean } {
  const override = typeof window !== 'undefined' ? window.__thottbotImportUrl : undefined;
  if (!override) return { action: IMPORT_ACTION, fields: [], overridden: false };
  const [path, query = ''] = override.split('?', 2);
  return {
    action: path ?? IMPORT_ACTION,
    fields: [...new URLSearchParams(query).entries()],
    overridden: true,
  };
}

function outcomeNotice(outcome: CharacterImportOutcome, count: number, label: string): string {
  switch (outcome) {
    case 'ok':
      return `Imported ${count} ${count === 1 ? 'character' : 'characters'}.`;
    case 'too_many':
      return `Imported the ${count} highest-level characters; the rest were left out.`;
    case 'none':
      return `Your Battle.net account has no characters in ${label}.`;
    case 'denied':
      return 'Battle.net access was not granted, so nothing was imported.';
    case 'mismatch':
      return 'That Battle.net account is not the one you signed in with.';
    case 'failed':
      return 'Battle.net did not answer; nothing changed. Try again in a minute.';
  }
}

function statusText(c: CharacterSummary): string {
  if (c.detailStatus === 'pending') return 'importing...';
  if (c.detailStatus === 'failed') return `failed${c.error ? ` - ${c.error}` : ''}`;
  return 'ready';
}

/** The "Characters" section of /me: import form, the list with public toggles, refresh and remove-all. */
export function MyCharacters() {
  const me = useMe();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const query = useMyCharacters(!!me.data?.user);
  const [region, setRegion] = useState<CharacterRegion>('us');
  const [confirmRemove, setConfirmRemove] = useState(false);

  // The checkbox is controlled, so the row flips synchronously here and the refetch confirms it.
  const [pendingPublic, setPendingPublic] = useState<Record<string, boolean>>({});
  const toggle = useMutation({
    mutationFn: ({ id, isPublic }: { id: string; isPublic: boolean }) =>
      apiSend<CharacterSummary>('PATCH', `/api/me/characters/${id}`, { public: isPublic }),
    onSuccess: (row) => {
      queryClient.setQueryData<MeCharactersResponse>(queryKeys.myCharacters, (old) =>
        old
          ? {
              ...old,
              characters: old.characters.map((c) =>
                c.id === row.id ? { ...c, public: row.public } : c,
              ),
            }
          : old,
      );
    },
    onSettled: (_row, _err, { id }) => {
      setPendingPublic((p) => {
        const next = { ...p };
        delete next[id];
        return next;
      });
      return queryClient.invalidateQueries({ queryKey: queryKeys.myCharacters });
    },
  });
  const setVisibility = (id: string, isPublic: boolean) => {
    setPendingPublic((p) => ({ ...p, [id]: isPublic }));
    toggle.mutate({ id, isPublic });
  };
  const removeAll = useMutation({
    mutationFn: () => apiSend<void>('DELETE', '/api/me/characters'),
    onSuccess: () => {
      setConfirmRemove(false);
      return queryClient.invalidateQueries({ queryKey: queryKeys.myCharacters });
    },
  });

  const rawOutcome = params.get('import');
  const outcome = (CHARACTER_IMPORT_OUTCOMES as readonly string[]).includes(rawOutcome ?? '')
    ? (rawOutcome as CharacterImportOutcome)
    : null;
  const data = query.data;
  const label = data?.settings.label ?? 'Classic Era';
  const { action, fields, overridden } = importAction();

  return (
    <section className="section" data-testid="my-characters">
      <h2>Characters</h2>
      {outcome && data && (
        <p
          className={`notice${outcome === 'ok' || outcome === 'too_many' ? '' : ' error'}`}
          data-testid="import-notice"
        >
          {outcomeNotice(outcome, data.characters.length, label)}
        </p>
      )}
      <form method="get" action={action} className="actions" data-testid="import-form">
        {fields.map(([name, value]) => (
          <input key={name} type="hidden" name={name} value={value} />
        ))}
        {!overridden && (
          <label>
            Region{' '}
            <select
              name="region"
              value={region}
              onChange={(e) => setRegion(e.target.value as CharacterRegion)}
            >
              {CHARACTER_REGIONS.map((r) => (
                <option key={r} value={r}>
                  {CHARACTER_REGION_LABELS[r]}
                </option>
              ))}
            </select>
          </label>
        )}
        <button type="submit" disabled={!!data?.importing}>
          Import my characters
        </button>
        {data && data.characters.length > 0 && (
          <button
            type="submit"
            disabled={!!data.importing}
            title="Replace every snapshot with what Battle.net has now"
          >
            Refresh
          </button>
        )}
        <span className="muted">Imports from {label}</span>
      </form>
      <p className="muted">
        Battle.net asks once for access to your characters; we keep a snapshot of each one (gear,
        enchants, talents), not the access itself. Refresh repeats that step.
      </p>
      {query.isPending && me.data?.user && <p className="muted">Loading...</p>}
      {data && data.characters.length === 0 && <p className="muted">No characters imported.</p>}
      {data && data.characters.length > 0 && (
        <>
          <div className="table-scroll">
            <table className="grid">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Level</th>
                  <th>Class</th>
                  <th>Race</th>
                  <th>Realm</th>
                  <th>Status</th>
                  <th>Visibility</th>
                  <th>Imported</th>
                </tr>
              </thead>
              <tbody>
                {data.characters.map((raw) => {
                  const c =
                    raw.id in pendingPublic ? { ...raw, public: pendingPublic[raw.id]! } : raw;
                  return (
                    <tr key={c.id} data-testid="my-character" data-status={c.detailStatus}>
                      <td>
                        <Link to={c.url}>{c.name}</Link>
                      </td>
                      <td>{c.level}</td>
                      <td>{c.class}</td>
                      <td>{c.race}</td>
                      <td>
                        {c.realmName} ({CHARACTER_REGION_LABELS[c.region]})
                      </td>
                      <td>{statusText(c)}</td>
                      <td>
                        <label>
                          <input
                            type="checkbox"
                            aria-label="Show publicly"
                            checked={c.public}
                            onChange={(e) => setVisibility(c.id, e.target.checked)}
                          />{' '}
                          {c.public ? 'Public' : 'Private'}
                        </label>
                      </td>
                      <td>{relativeTime(c.importedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="actions">
            {confirmRemove ? (
              <>
                <span>Remove every imported character and its public page?</span>
                <button
                  type="button"
                  onClick={() => removeAll.mutate()}
                  disabled={removeAll.isPending}
                >
                  Confirm remove
                </button>
                <button type="button" onClick={() => setConfirmRemove(false)}>
                  Keep them
                </button>
              </>
            ) : (
              <button type="button" onClick={() => setConfirmRemove(true)}>
                Remove all imported characters
              </button>
            )}
          </p>
        </>
      )}
    </section>
  );
}
